import { Button } from "@/components/ui/baseUi/button"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { useBoxSlotDeleteSession } from "@/modules/boxSlotSession/hooks/useBoxSlotDeleteSession"
import { useBoxSlotGetSession } from "@/modules/boxSlotSession/hooks/useBoxSlotGetSession"
import { cn } from "@/modules/lib/utils"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { ExerciseDetail } from "@/modules/sessions/sessionDetails/blocks/exerciseBlock/components/exerciseDetail"
import { MediaDetails } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetails"
import { NoteForAthletesDetails } from "@/modules/sessions/sessionDetails/blocks/noteBlock/components/noteForAthletesDetails"
import { NoteForCoachesDetails } from "@/modules/sessions/sessionDetails/blocks/noteBlock/components/noteForCoachesDetails"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { useBlockDuration } from "@/modules/sessions/sessionForm/hooks/useBlockDuration"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { useSessionCopyActions } from "@/modules/sessions/stores/useSessionCopyStore"
import { SocialCard } from "@/modules/social/components/socialCard"
import { defaultDateFormat } from "@/modules/utils/dateUtils"
import SessionHideEmpty from "@images/empty/session_hide_empty.svg"
import { CardioSelectedIcon } from "@images/svgIcons/cardioIcon"
import { CopyIcon } from "@images/svgIcons/copyIcon"
import { GymSelectedIcon } from "@images/svgIcons/gymIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { WeightliftingSelectedIcon } from "@images/svgIcons/weightliftingIcon"
import { BoxSlotSessionContentDetailsFullscreen } from "@modules/boxSlot/components/boxSlotSessionContentDetailsFullscreen"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { ScoreboardCard } from "@modules/scores/scoreboard/components/scoreboardCard"
import { useScoreboardSidebar } from "@modules/scores/scoreboard/hooks/useScoreboardSidebar"
import { ScoreType } from "@modules/scores/scoreTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { EmptyView } from "@modules/ui/components/emptyView"
import { FullscreenModal, type FullscreenModalRef } from "@modules/ui/components/fullScreenModal/fullscreenModal"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText } from "@modules/ui/components/richText/richTextUtils"
import { EditIcon } from "@modules/ui/icons/editIcon"
import dayjs from "dayjs"
import { ExpandIcon } from "lucide-react"
import { useMemo, useRef } from "react"

type BoxSlotSessionContentDetailsProps = {
  boxSessionId: string
  boxSlotId: string
} & React.ComponentProps<"div">

export function BoxSlotSessionContentDetails({
  boxSessionId,
  boxSlotId,
  className,
  ...props
}: BoxSlotSessionContentDetailsProps) {
  const { data: boxSession } = useBoxSlotGetSession(boxSessionId)
  const { data: boxProgrammation } = useBoxProgram(boxSession.programId)

  const confirmPopup = useConfirmPopup()
  const fullscreenModalRef = useRef<FullscreenModalRef>(null)
  const { mutate: deleteSession } = useBoxSlotDeleteSession()
  const { setSessionCopy } = useSessionCopyActions()
  const { navigateToSessionForm } = useSessionSidebar()
  const { hideSidebar } = useSidebar()
  const { navigateToScoreboardView } = useScoreboardSidebar()
  const { getBlockDurationInterval, timedBlocks, timedBlocksTotal } = useBlockDuration(boxSession.blocks)

  const canEditSession = useBoxStaffPermission(BoxStaffPermissions.MANAGE_PROGRAMMING)

  const scoreExercicesRequested: SessionBlockExerciseEntity[] = useMemo(() => {
    const listScoreExercicesRequest = boxSession.blocks
      .filter(
        (block) =>
          block.type === SessionBlockType.SESSION_EXERCISE &&
          block.content.scoreSetConfigurations.length > 0 &&
          !!block.content.scoreSetConfigurations[0] &&
          ![ScoreType.TEXT_FROM_ATHLETE, ScoreType.NO_SCORE].includes(
            block.content.scoreSetConfigurations[0].scoreType,
          ),
      )
      .map((block) => block.content as SessionBlockExerciseEntity)
    return listScoreExercicesRequest
  }, [boxSession])

  const onClickOnScoreboard = () => {
    navigateToScoreboardView(boxSession)
  }

  const editSession = useMemo(
    () => () => {
      void navigateToSessionForm(
        boxProgrammation,
        boxSession,
        dayjs(boxSession.date).format(defaultDateFormat),
        boxSlotId,
        { replaceAllComponents: false },
      )
    },
    [boxProgrammation, boxSession, boxSlotId, navigateToSessionForm],
  )

  const onSessionCopy = () => {
    try {
      setSessionCopy(boxSession)
      toast.success(word("program.session.copy.success"), { classNames: successToastClassName() })
    } catch {
      toast.error(word("program.session.copy.error"), { classNames: errorToastClassName() })
    }
  }

  const onDeleteSessionConfirmationPopup = () => {
    confirmPopup.show({
      title: word("sessionDetailed.deleteSession.popup.title"),
      message: word("boxSessionDetailed.deleteSession.popup.message", {
        sessionName: boxSession.name,
      }),
      accept: () => {
        try {
          deleteSession({ sessionId: boxSessionId })
          toast.success(word("program.session.delete.success"), { classNames: successToastClassName() })
          hideSidebar()
        } catch {
          toast.error(word("global.error.label"), { classNames: errorToastClassName() })
        }
      },
    })
  }

  // PERMISSIONS - Coach & Financial permissions denied
  const canUserSeeSessionContent = true // TODO manage permissions

  return (
    <div className={cn("flex grow flex-col gap-2", className)} {...props}>
      <FullscreenModal ref={fullscreenModalRef}>
        <BoxSlotSessionContentDetailsFullscreen boxSessionId={boxSessionId} />
      </FullscreenModal>

      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {!canUserSeeSessionContent ? (
        <EmptyView
          image={{ src: SessionHideEmpty }}
          title={word("session.empty.title.hideSession")}
          description={word("session.empty.description.hideSession")}
          flexGrow={false}
          className="bg-surface-foreground p-12"
          button={
            <Button variant="primary" onClick={() => hideSidebar()}>
              {word("global.button.wait")}
            </Button>
          }
          paddingType="In_table"
        />
      ) : (
        <div className="flex grow flex-col gap-2">
          <div className="flex flex-row justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Text fontStyle="HM" className="wrap-anywhere">
                {boxSession.name}
              </Text>
              {boxSession.date && <Text fontStyle="BM">{dayjs(boxSession.date).format("LL")}</Text>}
            </div>
            {canEditSession ? (
              <EllipsisMenu propsContent={{ side: "bottom" }}>
                <DropdownMenuItem key="edit" onClick={editSession}>
                  <EditIcon color="currentColor" className="text-textDefault-body" />
                  {word("global.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  key="copy"
                  onClick={() => {
                    onSessionCopy()
                  }}
                >
                  <CopyIcon color="currentColor" className="text-textDefault-body" />
                  {word("program.session.copy.label")}
                </DropdownMenuItem>

                <DropdownMenuItem
                  key="fullscreen"
                  onClick={() => {
                    fullscreenModalRef.current?.open()
                  }}
                >
                  <ExpandIcon color="currentColor" className="text-textDefault-body" />
                  {word("program.session.displayFullScreen")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  key="delete"
                  onClick={() => {
                    onDeleteSessionConfirmationPopup()
                  }}
                >
                  <TrashIcon color="currentColor" className="text-textError" />
                  <span className="text-textError">{word("global.delete")}</span>
                </DropdownMenuItem>
              </EllipsisMenu>
            ) : null}
          </div>
          <div className="flex flex-row items-center justify-start gap-2">
            {boxSession.gymnasticTag && (
              <GymSelectedIcon color={"black"} className="mr-2 mb-1" width={30} height={30} />
            )}
            {boxSession.weightliftingTag && (
              <WeightliftingSelectedIcon color={"black"} className="mr-2 mb-1" width={30} height={30} />
            )}
            {boxSession.cardioTag && <CardioSelectedIcon color={"black"} className="mb-1" width={30} height={30} />}
          </div>

          {boxSession.description && (
            <HURichTextView
              value={formatTextToRichText(boxSession.description)}
              paragrapheFontStyle="BM"
              className="mt-2"
            />
          )}

          <div className="mb-2">
            <SocialCard
              socialPostId={boxSession.socialPostId}
              sessionInfos={{ id: boxSession.id, name: boxSession.name }}
              socialResume={boxSession.socialResume ?? null}
            />
          </div>

          {scoreExercicesRequested.length > 0 && (
            <ScoreboardCard
              session={boxSession}
              onClick={() => onClickOnScoreboard()}
              canUserAccessToScoreboard={true}
              isAuthor={false}
              isAcceptedSharedCoach={false}
              isOwner
            />
          )}

          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {canUserSeeSessionContent && (
            <div className="flex w-full flex-col gap-5 pt-2">
              <div className="flex flex-row items-center justify-between">
                <Text fontStyle="LS" className="text-textDefault-caption uppercase">
                  {`${word("program.planning.create.exercise.block_plural", { count: boxSession.blocks.length })} (${boxSession.blocks.length.toString()})`}
                </Text>

                {timedBlocks.length > 0 && (
                  <div>
                    <Text
                      fontStyle="LS"
                      className="border-r border-neutral-300 pr-1 text-textDefault-caption uppercase"
                    >
                      {`${word("sessionForm.blocks.timed")} (${timedBlocks.length.toString()})`}
                    </Text>
                    <Text fontStyle="LS" className="pl-1 text-textDefault-caption">
                      {word("sessionForm.block.totalTime")}
                      <Text fontStyle="LS" className="pl-1 font-bold">
                        {timedBlocksTotal}
                      </Text>
                      {word("global.minute.shortLabel", { count: timedBlocksTotal })}
                    </Text>
                  </div>
                )}
              </div>

              {boxSession.blocks.map((block, index) => (
                <div key={`boxSlotSessionContentDetails-${index.toString()}`} className="flex w-full flex-col gap-2">
                  {(block.type === SessionBlockType.SESSION_EXERCISE || block.type === SessionBlockType.COACH_NOTE) &&
                    !!block.content.durationInMin && (
                      <Text fontStyle="BS" className="mb-1 flex flex-row items-center gap-2">
                        <div className="h-2 w-2 rounded-sm bg-neutral-700" />
                        {getBlockDurationInterval(index)}
                      </Text>
                    )}
                  {block.type === SessionBlockType.SESSION_EXERCISE ? (
                    <ExerciseDetail exercise={block.content} parentSession={null} isCoach={true} />
                  ) : block.type === SessionBlockType.COACH_NOTE ? (
                    <NoteForCoachesDetails key={index} note={block.content} />
                  ) : block.type === SessionBlockType.MEDIAS ? (
                    <MediaDetails key={index} medias={block.content.medias} />
                  ) : (
                    <NoteForAthletesDetails key={index} note={block.content} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
